import './personal.css';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Button } from '@openai/apps-sdk-ui/components/Button';
import { Input } from '@openai/apps-sdk-ui/components/Input';
import { Textarea } from '@openai/apps-sdk-ui/components/Textarea';
import { Checkbox } from '@openai/apps-sdk-ui/components/Checkbox';
import { Badge } from '@openai/apps-sdk-ui/components/Badge';
import {
  Bell,
  BookBookmark,
  CalendarToday,
  Clock,
  Document,
  ExternalLink,
  Lock,
  Plus,
  Search,
} from '@openai/apps-sdk-ui/components/Icon';
import type {
  NewPersonalItem,
  PersonalItem,
  PersonalItemKind,
  PersonalItemPatch,
  RecurrenceConfig,
  Project,
  SearchResults,
  Task,
  User,
  Channel,
  FollowUpSource,
} from '../shared/types';
import { FollowUpSourceLink, followUpSourceLabel } from './FollowUp';
import { MorningDigest } from './Notifications';
import { dateInZone, localDateTime, resolveSchedule, validatedTimeZone } from '../shared/datetime';
import { errorMessage } from './api';
import { RepeatBadge, RepeatControls, repeatChoice, type RepeatChoice } from './Recurrence';
import { Empty, ErrorNotice, Field, Modal, formatDate } from './ui';

export const ITEM_LABEL: Record<PersonalItemKind, string> = {
  reminder: 'Reminder',
  waiting: 'Waiting item',
  note: 'Note',
  bookmark: 'Bookmark',
};
const ItemIcon = ({ kind }: { kind: PersonalItemKind }) =>
  kind === 'reminder' ? (
    <Bell />
  ) : kind === 'waiting' ? (
    <Clock />
  ) : kind === 'bookmark' ? (
    <BookBookmark />
  ) : (
    <Document />
  );
export function PersonalLinks({
  navigate,
  active,
}: {
  navigate: (path: string) => void;
  active: string;
}) {
  return (
    <nav className="personal-links" aria-label="Personal tools">
      {[
        ['/today', 'Today'],
        ['/upcoming', 'Upcoming'],
        ['/forgotten', 'Forgotten'],
        ['/reminders', 'Reminders'],
        ['/waiting', 'Waiting'],
        ['/notes', 'Notes'],
        ['/bookmarks', 'Bookmarks'],
        ['/search', 'Search'],
      ].map(([path, label]) => (
        <button
          className={active === path ? 'active' : ''}
          key={path}
          onClick={() => navigate(path)}
        >
          {label}
        </button>
      ))}
    </nav>
  );
}
function scheduledLabel(item: PersonalItem) {
  return item.scheduledAt
    ? new Intl.DateTimeFormat([], {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: item.timeZone,
      }).format(new Date(item.scheduledAt))
    : 'No follow-up date';
}
function privateScope() {
  return (
    <span className="private-scope">
      <Lock />
      Only you
    </span>
  );
}

export function TodayPage({
  tasks,
  items,
  user,
  projects,
  timeZone,
  now,
  tab,
  navigate,
  onNewItem,
  onEditItem,
  onCompleteItem,
  onSnoozeItem,
  onEditTask,
  onCompleteTask,
  channels,
  users,
  onOpenSource,
}: {
  tasks: Task[];
  items: PersonalItem[];
  user: User;
  projects: Project[];
  timeZone: string;
  now: number;
  tab: 'today' | 'reminders' | 'waiting';
  navigate: (path: string) => void;
  onNewItem: (kind: PersonalItemKind) => void;
  onEditItem: (item: PersonalItem) => void;
  onCompleteItem: (item: PersonalItem, completed: boolean) => Promise<void>;
  onSnoozeItem: (item: PersonalItem) => Promise<void>;
  onEditTask: (task: Task) => void;
  onCompleteTask: (task: Task) => Promise<void>;
  channels: Channel[];
  users: User[];
  onOpenSource: (source: FollowUpSource) => void;
}) {
  const [showCompleted, setShowCompleted] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(new Set<string>());
  const pendingRef = useRef(new Set<string>());
  const today = dateInZone(new Date(now), timeZone);
  const mine = tasks.filter(
    (task) =>
      task.status !== 'done' &&
      (task.assigneeId === user.id || (!task.assigneeId && task.createdById === user.id)),
  );
  const taskGroups: [string, Task[]][] = [
    ['Overdue', mine.filter((task) => task.dueDate && task.dueDate < today)],
    ['Due today', mine.filter((task) => task.dueDate === today)],
    [
      'Upcoming',
      mine
        .filter((task) => task.dueDate && task.dueDate > today)
        .sort((a, b) => a.dueDate!.localeCompare(b.dueDate!)),
    ],
    ['No due date', mine.filter((task) => !task.dueDate)],
  ];
  const actionable = items.filter(
    (item) =>
      (item.kind === 'reminder' || item.kind === 'waiting') &&
      (tab === 'today' ? !item.completedAt : showCompleted || !item.completedAt),
  );
  const reminders = actionable
    .filter((item) => item.kind === 'reminder')
    .sort((a, b) => (a.scheduledAt || '').localeCompare(b.scheduledAt || ''));
  const waiting = actionable
    .filter((item) => item.kind === 'waiting')
    .sort((a, b) => (a.scheduledAt || 'z').localeCompare(b.scheduledAt || 'z'));
  async function act(id: string, action: () => Promise<void>) {
    if (pendingRef.current.has(id)) return;
    pendingRef.current.add(id);
    setPending(new Set(pendingRef.current));
    setError('');
    try {
      await action();
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      pendingRef.current.delete(id);
      setPending(new Set(pendingRef.current));
    }
  }
  function itemRows(list: PersonalItem[]) {
    return list.map((item) => (
      <div className={`personal-row ${item.completedAt ? 'item-complete' : ''}`} key={item.id}>
        <Checkbox
          checked={!!item.completedAt}
          disabled={pending.has(item.id) || (!!item.completedAt && !!item.recurrence)}
          onCheckedChange={(completed) => void act(item.id, () => onCompleteItem(item, completed))}
          label={
            <span className="sr-only">
              {item.completedAt && item.recurrence
                ? 'Completed occurrence'
                : item.completedAt
                  ? 'Reopen'
                  : item.kind === 'waiting'
                    ? 'Resolve'
                    : 'Complete'}{' '}
              {item.title}
            </span>
          }
        />
        <button className="personal-row-main" onClick={() => onEditItem(item)}>
          <strong>{item.title}</strong>
          <RepeatBadge recurrence={item.recurrence} />
          {item.counterparty && <small>Waiting for {item.counterparty}</small>}
          {item.body && <p>{item.body}</p>}
          <span
            className={
              item.scheduledAt && new Date(item.scheduledAt).getTime() <= now && !item.completedAt
                ? 'personal-date due'
                : 'personal-date'
            }
          >
            {scheduledLabel(item)} <span>· {item.timeZone}</span>
          </span>
        </button>
        <div className="personal-row-actions">
          {item.kind === 'waiting' && !item.completedAt && (
            <Button
              color="secondary"
              variant="ghost"
              size="sm"
              disabled={pending.has(item.id)}
              onClick={() => void act(item.id, () => onCompleteItem(item, true))}
            >
              Resolve
            </Button>
          )}
          {item.kind === 'waiting' && (
            <FollowUpSourceLink
              source={item.source}
              tasks={tasks}
              channels={channels}
              users={users}
              userId={user.id}
              open={onOpenSource}
            />
          )}
          {item.projectId && (
            <span className="personal-project">
              {projects.find((project) => project.id === item.projectId)?.name || 'Project'}
            </span>
          )}
          {!item.completedAt &&
            (item.kind === 'waiting' ||
              (item.scheduledAt && Date.parse(item.scheduledAt) <= now)) && (
              <Button
                color="secondary"
                variant="ghost"
                size="sm"
                disabled={pending.has(item.id)}
                onClick={() => void act(item.id, () => onSnoozeItem(item))}
              >
                Snooze 30m
              </Button>
            )}
        </div>
      </div>
    ));
  }
  const title = tab === 'today' ? 'Today' : tab === 'reminders' ? 'Reminders' : 'Waiting';
  return (
    <div className="personal-page page-enter">
      <div className="page-heading">
        <div>
          <span className="eyebrow">
            {tab === 'today'
              ? new Intl.DateTimeFormat([], {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  timeZone,
                }).format(new Date(now))
              : 'PRIVATE TRACKING'}
          </span>
          <h1>{title}</h1>
          <p>
            {tab === 'today'
              ? 'Your work and private follow-ups.'
              : tab === 'reminders'
                ? 'Private reminders, shown in the app when they are due.'
                : 'Private follow-ups for things you are waiting for. Chasing is manual; nobody is notified.'}
          </p>
        </div>
        <Button
          color="primary"
          size="lg"
          onClick={() => onNewItem(tab === 'waiting' ? 'waiting' : 'reminder')}
        >
          <Plus />
          {tab === 'waiting' ? 'Add waiting item' : 'New reminder'}
        </Button>
      </div>
      <PersonalLinks active={`/${tab}`} navigate={navigate} />
      <div className="personal-page-meta">
        {privateScope()}
        <span>{timeZone}</span>
        {tab !== 'today' && (
          <Checkbox
            checked={showCompleted}
            onCheckedChange={setShowCompleted}
            label={tab === 'waiting' ? 'Show resolved' : 'Show completed'}
          />
        )}
      </div>
      {error && <ErrorNotice message={error} />}
      {tab === 'today' ? (
        <>
          <MorningDigest key={user.id} />
          <PersonalSection
            title="Reminders"
            count={reminders.length}
            scope="Private"
            action={<button onClick={() => onNewItem('reminder')}>Add reminder</button>}
          >
            {reminders.length ? (
              itemRows(reminders)
            ) : (
              <p className="personal-empty-line">No open reminders.</p>
            )}
          </PersonalSection>
          <PersonalSection
            title="Waiting"
            count={waiting.length}
            scope="Private"
            action={<button onClick={() => onNewItem('waiting')}>Add waiting item</button>}
          >
            {waiting.length ? (
              itemRows(waiting)
            ) : (
              <p className="personal-empty-line">Nothing on your waiting list.</p>
            )}
          </PersonalSection>
          <div className="personal-task-heading">
            <h2>Your tasks</h2>
            <span>Assigned to you, or created by you and unassigned.</span>
          </div>
          {mine.length ? (
            taskGroups
              .filter(([, list]) => list.length)
              .map(([label, list]) => (
                <PersonalSection key={label} title={label} count={list.length} scope="Workspace">
                  {list.map((task) => (
                    <div className="personal-row" key={task.id}>
                      <Checkbox
                        disabled={pending.has(task.id)}
                        checked={false}
                        onCheckedChange={() => void act(task.id, () => onCompleteTask(task))}
                        label={<span className="sr-only">Complete {task.title}</span>}
                      />
                      <button className="personal-row-main" onClick={() => onEditTask(task)}>
                        <strong>{task.title}</strong>
                        <small>
                          {task.projectId
                            ? projects.find((project) => project.id === task.projectId)?.name ||
                              'Project'
                            : 'No project'}
                        </small>
                      </button>
                      {task.priority === 'high' && <Badge color="warning">High</Badge>}
                      {task.dueDate && (
                        <span className={`personal-date ${task.dueDate < today ? 'due' : ''}`}>
                          {formatDate(task.dueDate)}
                        </span>
                      )}
                    </div>
                  ))}
                </PersonalSection>
              ))
          ) : (
            <p className="personal-empty-line">No unfinished tasks assigned to you.</p>
          )}
        </>
      ) : (tab === 'reminders' ? reminders : waiting).length ? (
        <div className="personal-list">{itemRows(tab === 'reminders' ? reminders : waiting)}</div>
      ) : (
        <Empty
          icon={tab === 'reminders' ? <Bell /> : <Clock />}
          title={
            showCompleted
              ? 'No items found'
              : tab === 'reminders'
                ? 'No open reminders'
                : 'Nothing waiting'
          }
          description={
            tab === 'reminders'
              ? 'Add a reminder with a date, time, and timezone.'
              : 'Add an item to follow up on privately.'
          }
        >
          <Button
            color="primary"
            onClick={() => onNewItem(tab === 'waiting' ? 'waiting' : 'reminder')}
          >
            <Plus />
            {tab === 'waiting' ? 'Add waiting item' : 'Add reminder'}
          </Button>
        </Empty>
      )}
      <p className="personal-footnote">
        Reminders appear in this app while it is open. Waiting items are private tracking; they do
        not message anyone.
      </p>
    </div>
  );
}
function PersonalSection({
  title,
  count,
  scope,
  action,
  children,
}: {
  title: string;
  count: number;
  scope: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="personal-section">
      <div className="personal-section-heading">
        <h2>{title}</h2>
        <span>{count}</span>
        <small>{scope}</small>
        {action}
      </div>
      {children}
    </section>
  );
}

export function LibraryPage({
  items,
  projects,
  kind,
  navigate,
  onNewItem,
  onEditItem,
}: {
  items: PersonalItem[];
  projects: Project[];
  kind: 'note' | 'bookmark';
  navigate: (path: string) => void;
  onNewItem: (kind: PersonalItemKind) => void;
  onEditItem: (item: PersonalItem) => void;
}) {
  const [query, setQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const visible = items
    .filter(
      (item) =>
        item.kind === kind &&
        `${item.title} ${item.body} ${item.url || ''}`
          .toLowerCase()
          .includes(query.toLowerCase()) &&
        (projectFilter === 'all' || item.projectId === projectFilter),
    )
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id));
  return (
    <div className="personal-page library-page page-enter">
      <div className="page-heading">
        <div>
          <span className="eyebrow">PRIVATE LIBRARY</span>
          <h1>{kind === 'note' ? 'Notes' : 'Bookmarks'}</h1>
          <p>
            {kind === 'note'
              ? 'Keep private notes and link them to a project.'
              : 'Save links with private context and project links.'}
          </p>
        </div>
        <Button color="primary" size="lg" onClick={() => onNewItem(kind)}>
          <Plus />
          {kind === 'note' ? 'New note' : 'Add bookmark'}
        </Button>
      </div>
      <PersonalLinks active={kind === 'note' ? '/notes' : '/bookmarks'} navigate={navigate} />
      <div className="library-toolbar">
        {privateScope()}
        <div>
          <select
            aria-label="Filter library by project"
            value={projectFilter}
            onChange={(event) => setProjectFilter(event.target.value)}
          >
            <option value="all">All projects</option>
            {projects.map((project) => (
              <option value={project.id} key={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          <Input
            aria-label={`Search ${kind === 'note' ? 'notes' : 'bookmarks'}`}
            placeholder="Search your library…"
            startAdornment={<Search />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>
      {visible.length ? (
        <div className="library-list">
          {visible.map((item) => (
            <article className="library-row" key={item.id}>
              <span className="library-item-icon">
                <ItemIcon kind={item.kind} />
              </span>
              <div>
                <button className="library-title" onClick={() => onEditItem(item)}>
                  {item.title}
                </button>
                {item.body && <p>{item.body}</p>}
                {item.url && (
                  <a
                    className="bookmark-link"
                    href={safeLink(item.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {item.url}
                    <ExternalLink />
                  </a>
                )}
                <div className="library-item-meta">
                  <span>Updated {formatDate(item.updatedAt)}</span>
                  {item.projectId && (
                    <button onClick={() => navigate(`/projects/${item.projectId}`)}>
                      {projects.find((project) => project.id === item.projectId)?.name || 'Project'}
                    </button>
                  )}
                </div>
              </div>
              <Button color="secondary" variant="ghost" size="sm" onClick={() => onEditItem(item)}>
                Edit
              </Button>
            </article>
          ))}
        </div>
      ) : (
        <Empty
          icon={kind === 'note' ? <Document /> : <BookBookmark />}
          title={
            query || projectFilter !== 'all'
              ? 'No matching items'
              : kind === 'note'
                ? 'No notes yet'
                : 'No bookmarks yet'
          }
          description="Your library is private, including items linked to shared projects."
        >
          <Button color="primary" onClick={() => onNewItem(kind)}>
            <Plus />
            {kind === 'note' ? 'Create a note' : 'Save a bookmark'}
          </Button>
        </Empty>
      )}
    </div>
  );
}
function safeLink(value: string) {
  try {
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export function PersonalEditor({
  item,
  kind,
  latestItem,
  projects,
  timeZone,
  close,
  save,
  remove,
  configureRecurrence,
  openCurrentOccurrence,
  tasks,
  channels,
  users,
  userId,
  defaultSource = null,
  defaultTitle = '',
  defaultCounterparty = '',
  defaultProjectId = null,
  onOpenSource,
  onDraftChange,
}: {
  item: PersonalItem | null;
  kind: PersonalItemKind;
  latestItem?: PersonalItem;
  projects: Project[];
  timeZone: string;
  close: () => void;
  save: (payload: NewPersonalItem | PersonalItemPatch) => Promise<void>;
  remove?: () => void;
  configureRecurrence?: (
    recurrence: RecurrenceConfig | null,
    expectedVersion: number,
  ) => Promise<PersonalItem>;
  openCurrentOccurrence?: () => void;
  tasks: Task[];
  channels: Channel[];
  users: User[];
  userId: string;
  defaultSource?: FollowUpSource | null;
  defaultTitle?: string;
  defaultCounterparty?: string;
  defaultProjectId?: string | null;
  onOpenSource: (source: FollowUpSource) => void;
  onDraftChange: () => void;
}) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const [zone, setZone] = useState(item?.timeZone || timeZone);
  const [expectedVersion, setExpectedVersion] = useState(item?.version || 0);
  const originalSource = item?.source || defaultSource;
  const [sourceValue, setSourceValue] = useState(
    originalSource ? `${originalSource.kind}:${originalSource.id}` : '',
  );
  const conflict = !!item && latestItem?.version !== expectedVersion;
  const scheduled = kind === 'reminder' || kind === 'waiting';
  const form = useRef<HTMLFormElement>(null);
  const [frequency, setFrequency] = useState<RepeatChoice>(repeatChoice(item?.recurrence));
  const current = latestItem || item;
  const historical = !!current?.recurrence && !!current.completedAt;
  const repeatDirty =
    !!item &&
    kind === 'reminder' &&
    (frequency !== repeatChoice(current?.recurrence) ||
      (frequency !== 'off' && !!current?.recurrence && zone !== current.recurrence.timeZone));
  const scheduleBaseline = useRef({
    scheduledAt: item?.scheduledAt || null,
    timeZone: item?.timeZone || timeZone,
  });
  function recurrenceConfig(fields: Record<string, FormDataEntryValue>): RecurrenceConfig | null {
    if (frequency === 'off') return null;
    const wall = String(fields.schedule || '');
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(wall))
      throw new Error('Choose a date and time before enabling repeat.');
    return {
      frequency,
      timeZone: validatedTimeZone(zone),
      anchorDate: wall.slice(0, 10),
      localTime: wall.slice(11, 16),
    };
  }
  async function applyRepeat() {
    if (!configureRecurrence || running.current || conflict || historical) return;
    setError('');
    let recurrence: RecurrenceConfig | null;
    try {
      recurrence = recurrenceConfig(Object.fromEntries(new FormData(form.current!)));
    } catch (error) {
      setError(errorMessage(error));
      return;
    }
    running.current = true;
    setBusy(true);
    try {
      const saved = await configureRecurrence(recurrence, expectedVersion);
      setExpectedVersion(saved.version);
      setFrequency(repeatChoice(saved.recurrence));
      setZone(saved.timeZone);
      scheduleBaseline.current = { scheduledAt: saved.scheduledAt, timeZone: saved.timeZone };
      if (recurrence && saved.scheduledAt) {
        const input = form.current?.querySelector<HTMLInputElement>('input[name="schedule"]');
        if (input) input.value = localDateTime(new Date(saved.scheduledAt), saved.timeZone);
      }
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (running.current || conflict || historical || repeatDirty) return;
    setError('');
    const fields = Object.fromEntries(new FormData(event.currentTarget));
    let payload: NewPersonalItem | PersonalItemPatch;
    try {
      const chosenZone = validatedTimeZone(zone);
      const scheduleText = String(fields.schedule || '');
      if (kind === 'waiting' && !item && !scheduleText)
        throw new Error('Choose when to follow up.');
      const sourceParts = sourceValue.split(':');
      const source = sourceValue
        ? { kind: sourceParts[0] as FollowUpSource['kind'], id: sourceParts[1] }
        : null;
      const sourceChanged = JSON.stringify(source) !== JSON.stringify(item?.source || null);
      if (
        kind === 'waiting' &&
        source &&
        sourceChanged &&
        !followUpSourceLabel(source, tasks, channels, users, userId)
      )
        throw new Error('This source is no longer available. Choose another source or unlink it.');
      const unchangedSchedule =
        !!scheduleBaseline.current.scheduledAt &&
        scheduleText ===
          localDateTime(
            new Date(scheduleBaseline.current.scheduledAt),
            scheduleBaseline.current.timeZone,
          ) &&
        chosenZone === validatedTimeZone(scheduleBaseline.current.timeZone);
      const scheduleInstant = scheduleText
        ? unchangedSchedule
          ? scheduleBaseline.current.scheduledAt
          : resolveSchedule(scheduleText, chosenZone)
        : null;
      payload = {
        title: String(fields.title).trim(),
        body: String(fields.body || '').trim(),
        projectId: String(fields.projectId || '') || null,
        timeZone: chosenZone,
        ...(kind === 'waiting' && (!item || sourceChanged) ? { source } : {}),
        ...(scheduled
          ? {
              scheduledAt: scheduleInstant,
              counterparty:
                kind === 'waiting' ? String(fields.counterparty || '').trim() || null : null,
            }
          : { url: kind === 'bookmark' ? String(fields.url || '').trim() : null }),
        ...(item
          ? { expectedVersion }
          : {
              kind,
              ...(kind === 'reminder' && frequency !== 'off'
                ? { recurrence: recurrenceConfig(fields)! }
                : {}),
            }),
      };
    } catch (error) {
      setError(errorMessage(error));
      return;
    }
    running.current = true;
    setBusy(true);
    try {
      await save(payload);
    } catch (error) {
      setError(errorMessage(error));
    } finally {
      running.current = false;
      setBusy(false);
    }
  }
  return (
    <Modal
      title={`${item ? 'Edit' : 'New'} ${ITEM_LABEL[kind].toLowerCase()}`}
      close={() => {
        if (!running.current) close();
      }}
    >
      <form
        ref={form}
        className="stack-form"
        onSubmit={submit}
        onInput={onDraftChange}
        onChange={onDraftChange}
        aria-busy={busy}
      >
        {historical && (
          <div className="repeat-history-notice">
            <RepeatBadge recurrence={current?.recurrence} />
            <p>
              This completed reminder occurrence is history. Open the current occurrence to change
              or stop future repeats.
            </p>
            {openCurrentOccurrence && (
              <Button color="secondary" variant="outline" size="sm" onClick={openCurrentOccurrence}>
                Open current occurrence
              </Button>
            )}
          </div>
        )}
        <div className="editor-private-notice">
          <Lock />
          <span>
            Only you can see this {ITEM_LABEL[kind].toLowerCase()}.
            {kind === 'waiting'
              ? ' Following up is manual. Linking a source sends no message and notifies nobody.'
              : ''}
          </span>
        </div>
        <Field label="Title">
          <Input
            name="title"
            required
            maxLength={240}
            defaultValue={item?.title || defaultTitle}
            placeholder={
              kind === 'reminder'
                ? 'What do you need to remember?'
                : kind === 'waiting'
                  ? 'What are you waiting for?'
                  : 'Give it a title'
            }
            size="lg"
            autoFocus
            readOnly={busy || historical}
          />
        </Field>
        {kind === 'bookmark' && (
          <Field label="URL">
            <Input
              name="url"
              type="url"
              required
              maxLength={2048}
              defaultValue={item?.url || ''}
              placeholder="https://…"
              readOnly={busy || historical}
            />
          </Field>
        )}
        {kind === 'waiting' && (
          <>
            <Field
              label="Linked source (optional)"
              hint="Link a task, channel, or teammate for context. Only you can see this follow-up."
            >
              <select
                aria-label="Linked follow-up source"
                value={sourceValue}
                disabled={busy || historical}
                onChange={(event) => setSourceValue(event.target.value)}
              >
                <option value="">No linked source</option>
                {sourceValue === `dm:${userId}` && (
                  <option value={sourceValue}>Personal notes (you)</option>
                )}
                {sourceValue &&
                  !followUpSourceLabel(
                    {
                      kind: sourceValue.split(':')[0] as FollowUpSource['kind'],
                      id: sourceValue.split(':')[1],
                    },
                    tasks,
                    channels,
                    users,
                    userId,
                  ) && (
                    <option value={sourceValue}>
                      Linked source unavailable · choose another or unlink
                    </option>
                  )}
                <optgroup label="Tasks">
                  {tasks.map((task) => (
                    <option key={task.id} value={`task:${task.id}`}>
                      {task.title}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Channels">
                  {channels.map((channel) => (
                    <option key={channel.id} value={`channel:${channel.id}`}>
                      #{channel.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="People">
                  {users
                    .filter((user) => user.id !== userId)
                    .map((user) => (
                      <option key={user.id} value={`dm:${user.id}`}>
                        {user.name}
                      </option>
                    ))}
                </optgroup>
              </select>
            </Field>
            {current?.source && (
              <FollowUpSourceLink
                source={current.source}
                tasks={tasks}
                channels={channels}
                users={users}
                userId={userId}
                open={onOpenSource}
              />
            )}
            <Field label="Waiting for (optional)" hint="A name or context for your own tracking.">
              <Input
                name="counterparty"
                maxLength={160}
                defaultValue={item?.counterparty || defaultCounterparty}
                placeholder="e.g. A reply from Sam"
                readOnly={busy || historical}
              />
            </Field>
          </>
        )}
        <Field label={kind === 'note' ? 'Note' : 'Details (optional)'}>
          <Textarea
            name="body"
            defaultValue={item?.body || ''}
            maxLength={10000}
            rows={kind === 'note' ? 7 : 3}
            placeholder="Add context…"
            readOnly={busy || historical}
          />
        </Field>
        {scheduled && (
          <div className="form-grid">
            <Field
              label={
                kind === 'reminder'
                  ? 'Date and time'
                  : item
                    ? 'Follow-up date (optional)'
                    : 'Follow-up date and time'
              }
            >
              <Input
                name="schedule"
                type="datetime-local"
                required={kind === 'reminder' || (kind === 'waiting' && !item)}
                defaultValue={
                  item?.scheduledAt ? localDateTime(new Date(item.scheduledAt), item.timeZone) : ''
                }
                readOnly={busy || historical}
              />
            </Field>
            <Field label="Timezone" hint="Date and time use this timezone.">
              <Input
                value={zone}
                onChange={(event) => setZone(event.target.value)}
                maxLength={100}
                required
                readOnly={busy || historical}
                placeholder="Europe/London"
                list="personal-timezones"
              />
              <datalist id="personal-timezones">
                {[
                  'Europe/London',
                  'Europe/Paris',
                  'America/New_York',
                  'America/Los_Angeles',
                  'Asia/Singapore',
                  'Australia/Sydney',
                  'UTC',
                ].map((value) => (
                  <option value={value} key={value} />
                ))}
              </datalist>
            </Field>
          </div>
        )}
        {kind === 'reminder' && (
          <RepeatControls
            value={frequency}
            onChange={setFrequency}
            busy={busy || conflict}
            existing={!!item}
            active={!!current?.recurrence?.active}
            historical={historical}
            apply={() => void applyRepeat()}
          />
        )}
        {current?.recurrence?.active && !historical && (
          <p className="repeat-occurrence-note">
            Saving the date changes only this occurrence. Apply repeat uses the date, time, and
            timezone above to reanchor future occurrences.
          </p>
        )}
        {repeatDirty && !historical && (
          <p className="repeat-pending" role="status">
            Apply repeat changes before saving reminder details. The repeat action uses the date,
            time, and timezone above.
          </p>
        )}
        <Field
          label="Project (optional)"
          hint="The item stays private when linked to a shared project."
        >
          <select
            name="projectId"
            defaultValue={item?.projectId || defaultProjectId || ''}
            disabled={busy || historical}
          >
            <option value="">No project</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
        </Field>
        {conflict && !historical && (
          <div className="version-conflict" role="status">
            <p>
              {latestItem
                ? 'This item changed elsewhere. Your draft is preserved.'
                : 'This item is no longer available. Close this dialog to review your library.'}
            </p>
            {latestItem && (
              <details>
                <summary>Review latest saved item</summary>
                <strong>{latestItem.title}</strong>
                <p>{latestItem.body || latestItem.url || ''}</p>
                {latestItem.scheduledAt && (
                  <p>
                    {scheduledLabel(latestItem)} · {latestItem.timeZone}
                  </p>
                )}
                <Button
                  color="secondary"
                  variant="outline"
                  size="sm"
                  onClick={() => setExpectedVersion(latestItem.version)}
                >
                  Keep my draft and use latest version
                </Button>
              </details>
            )}
          </div>
        )}
        {error && <ErrorNotice message={error} />}
        <div className="modal-actions">
          {remove && !historical && (
            <Button
              color="danger"
              variant="ghost"
              className="editor-delete-action"
              onClick={remove}
              disabled={busy || conflict}
            >
              {current?.recurrence?.active ? 'Delete current reminder & stop repeat' : 'Delete'}
            </Button>
          )}
          <span className="spacer" />
          <Button color="secondary" variant="outline" onClick={close} disabled={busy}>
            {historical ? 'Close' : 'Cancel'}
          </Button>
          {!historical && (
            <Button color="primary" type="submit" loading={busy} disabled={conflict || repeatDirty}>
              {item ? 'Save changes' : `Create ${ITEM_LABEL[kind].toLowerCase()}`}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

export function SearchPage({
  initialQuery,
  revision,
  search,
  navigate,
  onEditTask,
  onEditItem,
}: {
  initialQuery: string;
  revision: number;
  search: (query: string) => Promise<SearchResults>;
  navigate: (path: string) => void;
  onEditTask: (task: Task) => void;
  onEditItem: (item: PersonalItem) => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchResults | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const serial = useRef(0);
  const submittedQuery = useRef(initialQuery);
  const lastRevision = useRef(revision);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      serial.current++;
    };
  }, []);
  async function run(value: string) {
    submittedQuery.current = value;
    const id = ++serial.current;
    if (!value.trim()) {
      setResults(null);
      setBusy(false);
      setError('');
      return;
    }
    setBusy(true);
    setResults(null);
    setError('');
    try {
      const response = await search(value.trim());
      if (mounted.current && id === serial.current) setResults(response);
    } catch (error) {
      if (mounted.current && id === serial.current) setError(errorMessage(error));
    } finally {
      if (mounted.current && id === serial.current) setBusy(false);
    }
  }
  useEffect(() => {
    setQuery(initialQuery);
    void run(initialQuery);
  }, [initialQuery]);
  useEffect(() => {
    if (lastRevision.current !== revision) {
      lastRevision.current = revision;
      if (submittedQuery.current.trim()) void run(submittedQuery.current);
    }
  }, [revision]);
  const total = results ? results.tasks.length + results.projects.length + results.items.length : 0;
  return (
    <div className="personal-page search-page page-enter">
      <div className="page-heading">
        <div>
          <span className="eyebrow">WORKSPACE & PRIVATE ITEMS</span>
          <h1>Search</h1>
          <p>
            Find workspace tasks and projects, plus your private items. Messages are not searched.
          </p>
        </div>
      </div>
      <PersonalLinks active="/search" navigate={navigate} />
      <form
        className="personal-search-form"
        onSubmit={(event) => {
          event.preventDefault();
          void run(query);
        }}
      >
        <Input
          aria-label="Search workspace and private items"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search tasks, projects, notes, bookmarks…"
          startAdornment={<Search />}
          size="xl"
          maxLength={200}
        />
        <Button type="submit" color="primary" loading={busy} disabled={!query.trim()}>
          Search
        </Button>
      </form>
      {error && <ErrorNotice message={error} />}
      <div aria-live="polite" className="search-summary">
        {busy
          ? 'Searching…'
          : results
            ? `${total} result${total === 1 ? '' : 's'}${results.truncated ? ' shown' : ''}`
            : 'Enter a search to get started.'}
      </div>
      {results && (
        <>
          {results.truncated && (
            <p className="personal-footnote">
              Showing up to 50 results in each type. Refine your search to find more.
            </p>
          )}
          {results.tasks.length > 0 && (
            <PersonalSection title="Tasks" count={results.tasks.length} scope="Workspace">
              {results.tasks.map((task) => (
                <button className="search-result" key={task.id} onClick={() => onEditTask(task)}>
                  <span>
                    <strong>{task.title}</strong>
                    <RepeatBadge recurrence={task.recurrence} />
                    <small>{task.description || 'No description'}</small>
                  </span>
                  <Badge color="secondary">
                    {task.status === 'done'
                      ? 'Done'
                      : task.status === 'in_progress'
                        ? 'In progress'
                        : 'To do'}
                  </Badge>
                </button>
              ))}
            </PersonalSection>
          )}
          {results.projects.length > 0 && (
            <PersonalSection title="Projects" count={results.projects.length} scope="Workspace">
              {results.projects.map((project) => (
                <button
                  className="search-result"
                  key={project.id}
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  <span>
                    <strong>{project.name}</strong>
                    <small>{project.description || 'No description'}</small>
                  </span>
                </button>
              ))}
            </PersonalSection>
          )}
          {results.items.length > 0 && (
            <PersonalSection title="Your items" count={results.items.length} scope="Private">
              {results.items.map((item) => (
                <button className="search-result" key={item.id} onClick={() => onEditItem(item)}>
                  <span>
                    <strong>{item.title}</strong>
                    <small>{item.body || item.url || ITEM_LABEL[item.kind]}</small>
                  </span>
                  <Badge color="secondary">{ITEM_LABEL[item.kind]}</Badge>
                </button>
              ))}
            </PersonalSection>
          )}
          {total === 0 && (
            <Empty
              icon={<Search />}
              title="No matches found"
              description="Try a different word or a shorter search."
            />
          )}
        </>
      )}
    </div>
  );
}
